import type { BookIndexChunk } from '@/types/search'
import { audioCacheKey, type AudioChunkRow } from '@/types/audio'
import * as repo from '@/db/repositories'
import { synthesize } from '@/ai/tts'
import { useSettingsStore } from '@/stores/settings'
import type { PlaybackEngine } from '@/stores/audioPlayer'

/**
 * Motor neural del modo audiolibro.
 *
 * El guion sale de la tabla `bookIndex` que la búsqueda global ya construye
 * al importar: fragmentos de ~2000 caracteres con `spineIndex`/`chapterHref`,
 * ordenables. Eso es exactamente una cola de audiolibro, y 2000 caracteres
 * caben en el límite de 4096 de la API. Por eso este motor NO necesita la
 * vista de foliate y sobrevive a la navegación.
 *
 * Coste: se sintetiza el fragmento actual y se **precarga solo el siguiente**.
 * Precargar más sería gastar dinero por adelantado en audio que el usuario
 * puede no llegar a escuchar.
 */

/** Ordena los fragmentos de un libro como se leen. Puro: testeable. */
export function sortBookChunks(chunks: BookIndexChunk[]): BookIndexChunk[] {
  return chunks
    .filter((c) => c.kind === 'book')
    .sort((a, b) => {
      const spine = (a.spineIndex ?? 0) - (b.spineIndex ?? 0)
      return spine !== 0 ? spine : a.chunkIndex - b.chunkIndex
    })
}

/**
 * Cola mínima de audio (segundos) que debe quedar por sonar para que un salto
 * hacia delante se considere «dentro del fragmento».
 *
 * No es un margen de seguridad al que acotar el destino: **acotar no sirve**.
 * Colocar `currentTime` en la duración exacta dispara `'ended'` en el acto, y
 * dejarlo a un pelo del final no arregla nada porque el `<audio>` SIGUE
 * SONANDO: llega al final solo unos milisegundos después (la mitad a 2×) y el
 * navegador dispara `'ended'` igual. Un margen fijo no evita el cruce, lo
 * retrasa. Por eso el salto que no cabe entero no se hace: ver `seek()`.
 */
const SEEK_MIN_TAIL_S = 0.25

/**
 * Fragmento por el que empezar a escuchar, dado el progreso guardado del libro
 * (`book.progress.percent`) y el tamaño de la cola. Puro: testeable.
 *
 * PRD, historia 6: «retomar leyendo justo donde dejé de escuchar (y al revés)».
 * La ida ya la cubría `persistNeuralProgress`, que guarda el CFI al avanzar de
 * fragmento; esta es la vuelta.
 *
 * Invierte el `percentAt(pos, count) = round((pos+1)/count · 100)` del store,
 * que es como escribe el progreso el propio audiolibro: para escuchar →
 * escuchar el fragmento que sale es exactamente el que se estaba oyendo.
 *
 * Un libro al 100 % vuelve a empezar en vez de arrancar en el último
 * fragmento, donde escuchar sería un fragmento y `onEnd()`: el reproductor se
 * cerraría solo a los dos minutos de darle a play.
 *
 * ponytail: cuando el progreso lo escribió el LECTOR, esto cae a uno o dos
 * fragmentos (~2-4 min) de donde tocaba: foliate mide la fracción por tamaño
 * renderizado y la cola por caracteres, y un punto de porcentaje son varios
 * fragmentos en un libro largo. Es el precio de no volver a abrir el EPUB al
 * arrancar. Vía de mejora si se nota: invertir `resolveChunkCfi` —parsear el
 * CFI guardado, sacar el `spineIndex` y localizar el fragmento que contiene
 * ese punto—, que cae exacto venga de donde venga.
 */
export function startChunk(percent: number, count: number): number {
  if (count === 0) return 0
  if (!Number.isFinite(percent) || percent <= 0 || percent >= 100) return 0
  return Math.min(count - 1, Math.max(0, Math.round((percent / 100) * count) - 1))
}

/** Tope mínimo y máximo del caché de audio, en MB (los del input de Ajustes). */
export const CACHE_LIMIT_MIN_MB = 100
export const CACHE_LIMIT_MAX_MB = 5000

/**
 * Acota el tope del caché a un rango sano. Puro: testeable.
 *
 * Un 0 (vaciar el campo de Ajustes da `Number('') === 0`) llega hasta
 * `evictAudioCache`, que desalojaría TODO el audio ya pagado salvo el
 * fragmento en curso: es el único punto de la feature que borra datos del
 * usuario, es irreversible y recuperarlo cuesta dinero real. Por eso se
 * valida en el borde de entrada (Ajustes) Y aquí, que es quien de verdad
 * decide cuánto se conserva: un valor inválido también puede llegar de un
 * backup importado o de una versión anterior.
 */
export function clampCacheLimitMb(mb: number): number {
  if (!Number.isFinite(mb)) return CACHE_LIMIT_MIN_MB
  return Math.min(Math.max(mb, CACHE_LIMIT_MIN_MB), CACHE_LIMIT_MAX_MB)
}

export interface AudioBookCallbacks {
  /** Empieza a sonar un fragmento (para progreso y UI). */
  onChunk(chunk: BookIndexChunk, position: number, total: number): void
  /** Fragmento en un capítulo distinto (temporizador 'fin de capítulo'). */
  onChapter(href: string | null): void
  /** Se acabó el libro. */
  onEnd(): void
  /** Fallo de red o de la API: la posición se conserva. */
  onError(e: unknown): void
}

export interface AudioBookEngine extends PlaybackEngine {
  /**
   * Este motor SIEMPRE sabe saltar (en `PlaybackEngine` es opcional porque el
   * motor navegador no puede: `speechSynthesis` no expone posición). Se
   * redeclara obligatorio para que quien tenga un `AudioBookEngine` en la mano
   * no tenga que comprobarlo.
   */
  seek(seconds: number): void
  /** Carga la cola del libro. Devuelve el nº de fragmentos (0 = sin indexar). */
  load(bookId: string): Promise<number>
  /**
   * De qué libro es la cola que el motor tiene AHORA MISMO cargada, o `null`
   * si de ninguno — porque nunca se cargó, porque el libro no daba fragmentos
   * o porque hay una carga en vuelo (llamar a `load` invalida la cola vieja en
   * el acto, aunque los fragmentos nuevos tarden en llegar).
   *
   * Existe porque el número que devuelve `load()` NO dice si esa cola llegó a
   * aplicarse: una carga abandonada (otra empezó después, o un `stop()` entró
   * en medio) devuelve su cuenta igual pero deja la cola como estaba. Quien
   * quiera saber de quién es la cola tiene que preguntárselo al motor, que es
   * donde vive; llevar la cuenta por fuera es lo que ha reabierto el mismo bug
   * por cuatro puertas distintas.
   */
  queuedBook(): string | null
}

export function useAudioBook(cb: AudioBookCallbacks): AudioBookEngine {
  const settings = useSettingsStore()
  const audio = new Audio()
  let chunks: BookIndexChunk[] = []
  /** Último libro que se pidió cargar, tenga o no fragmentos. */
  let bookId: string | null = null
  /** Libro de la cola realmente aplicada (ver `queuedBook`). */
  let queuedBookId: string | null = null
  let pos = 0
  let lastChapter: string | null = null
  /**
   * Peticiones de audio en vuelo, por clave de caché.
   *
   * Guardar la PROMESA (no el blob resuelto) es lo que evita pagar dos veces
   * el mismo fragmento: dos llamadas concurrentes a `blobFor()` con la misma
   * clave verían ambas "no cacheado" —nada se ha escrito aún— y sintetizarían
   * por separado. La ventana no es sub-segundo: es la latencia real de la
   * API, segundos, y basta con pulsar anterior/siguiente mientras el primer
   * fragmento se sintetiza (la UI deshabilita esos botones en 'idle' y
   * mientras se prepara la cola, pero NO durante la síntesis de un fragmento:
   * ahí el estado ya es 'playing').
   *
   * Un mapa y no el "slot" único de la precarga porque el slot solo cubría
   * precarga-vs-reproducción, no dos reproducciones concurrentes del mismo
   * fragmento. La entrada se borra al resolver o fallar, para que un fallo
   * se pueda reintentar.
   */
  const inFlight = new Map<string, Promise<Blob>>()
  /** ObjectURL en curso, para revocarlo y no filtrar memoria. */
  let currentUrl: string | null = null
  let rate = 1
  /**
   * Generación de la reproducción en curso. La incrementan `load()`, `stop()`
   * y cada `playCurrent()`: quien empieza toma el mando y todo lo que
   * estuviera en vuelo se rinde en cuanto vuelve de su `await`.
   *
   * Sin esto, un `playCurrent` que espera la síntesis del fragmento —segundos
   * de latencia real de la API— sobrevive a que el usuario cambie de libro o
   * salte de fragmento: al resolver pone SU blob en el `<audio>`, lo
   * reproduce encima de lo que ya sonaba y llama a `cb.onChunk` con un
   * fragmento de la cola vieja pero la posición y el total de la nueva
   * (progreso corrupto en el libro anterior). El `Map` de `inFlight` no cubre
   * nada de esto: deduplica la MISMA clave, no dos fragmentos distintos
   * peleándose el mismo `<audio>`.
   *
   * También ordena dos `load()` concurrentes: gana el que se pidió último,
   * resuelva cuando resuelva.
   */
  let generation = 0
  /**
   * El usuario ha pausado y todavía no ha vuelto a pedir audio.
   *
   * Hace falta aparte de `generation` porque pausar NO es abandonar la
   * reproducción: el fragmento sigue siendo el que toca sonar. Si `pause()`
   * subiera la generación, el `playCurrent` en vuelo se rendiría dejando en
   * el `<audio>` el `src` del fragmento ANTERIOR, y `resume()` —que reanuda
   * ese `src` si lo hay— repetiría el fragmento de antes y, al terminar,
   * saltaría al siguiente: el que estaba a medio sintetizar se perdería para
   * siempre. Con la bandera, el bail vacía el `<audio>` y `resume()` vuelve a
   * arrancar ESTE fragmento, que ya está pagado y en caché.
   *
   * Sin nada de esto, pulsar pausa durante los segundos de latencia de la API
   * era un no-op (`audio.pause()` sobre un `<audio>` sin `src`) y el audio
   * arrancaba solo al resolver la síntesis, con el store en 'paused'.
   */
  let paused = false

  audio.addEventListener('ended', () => { void next() })

  /**
   * Voz con la que sintetizar, según el proveedor de TTS elegido. Entra en la
   * clave de caché (`audioCacheKey`), así que cambiar de proveedor no
   * reproduce el audio del otro: los IDs de ElevenLabs no chocan con los
   * nombres de OpenAI ('nova', 'onyx'…).
   */
  function voice(): string {
    const { ai, audio } = settings.settings
    return ai.ttsProvider === 'elevenlabs' ? audio.elevenLabsVoice : audio.neuralVoice
  }

  function limitBytes(): number {
    return clampCacheLimitMb(settings.settings.audio.cacheLimitMb) * 1024 * 1024
  }

  /**
   * Audio de un fragmento: del caché si está, sintetizado si no. Una
   * petición en vuelo para la misma clave se reclama en vez de duplicarse
   * (ver `inFlight`: cada duplicado es dinero real del usuario).
   */
  function blobFor(chunk: BookIndexChunk): Promise<Blob> {
    // La voz se captura UNA vez y viaja hasta el final. `fetchOrSynthesize`
    // consulta el caché antes de sintetizar, así que volver a leer los ajustes
    // allí es leerlos después de un `await`: si el usuario cambia de voz en
    // esa ventana, el audio de la voz NUEVA se guardaba bajo la clave de la
    // VIEJA. Esa clave pasa a devolver una voz que no es, y el audio de la
    // nueva no lo encuentra nadie — se paga otra vez.
    const chunkVoice = voice()
    const key = audioCacheKey(chunk.id, chunkVoice)
    const existing = inFlight.get(key)
    if (existing) return existing
    const promise = fetchOrSynthesize(chunk, key, chunkVoice)
      .finally(() => { inFlight.delete(key) })
    inFlight.set(key, promise)
    return promise
  }

  /** Trabajo real de `blobFor`, sin la deduplicación. */
  async function fetchOrSynthesize(
    chunk: BookIndexChunk,
    key: string,
    chunkVoice: string,
  ): Promise<Blob> {
    const cached = await repo.getAudioChunk(key)
    if (cached) {
      await repo.touchAudioChunk(key)
      return cached.blob
    }
    const blob = await synthesize(chunk.text, chunkVoice, settings.settings.ai)
    const row: AudioChunkRow = {
      id: key,
      bookId: chunk.bookId,
      blob,
      bytes: blob.size,
      lastUsedAt: new Date().toISOString(),
    }
    // Guardar es best-effort, y va DESPUÉS de tener el blob en la mano a
    // propósito: para cuando se llega aquí el audio ya está sintetizado y
    // COBRADO. Si IndexedDB no admite la escritura (cuota llena, modo
    // privado), propagar el fallo lo mandaría al `catch` de `playCurrent`,
    // que lo trata como un fallo de síntesis: pausa, toast de error y el
    // fragmento pagado a la basura. Se avisa por consola y a sonar.
    try {
      await repo.putAudioChunk(row)
      // Desalojar dejando a salvo lo que acabamos de meter (va a sonar ya).
      await repo.evictAudioCache(limitBytes(), key)
    } catch (e) {
      console.warn('[audio] no se pudo guardar el fragmento en el caché:', e)
    }
    return blob
  }

  /** Sintetiza el fragmento siguiente en segundo plano, sin bloquear. */
  function prefetchNext() {
    const upcoming = chunks[pos + 1]
    if (!upcoming) return
    // Si nadie reclama esta promesa (el usuario salta más allá antes de que
    // termine), evita un "unhandled rejection": el fallo real se gestiona
    // si (y cuando) `playCurrent` la reclama. Un fallo de precarga en sí no
    // es del usuario: se reintentará al llegar.
    blobFor(upcoming).catch(() => {})
  }

  /**
   * ¿Son los fragmentos que hay en memoria los del libro que se pidió?
   *
   * Mientras un `load()` está en vuelo, NO: son los del libro anterior, que
   * `load` no vacía al entrar (a propósito: si esa carga se abandona, la cola
   * de antes sigue ahí y se puede volver a ella). Sobre una cola ajena no se
   * puede ni reproducir —sería pagar dinero real por el libro equivocado— ni
   * sacar conclusiones, como que el libro se ha terminado.
   */
  function queueIsCurrent(): boolean {
    return queuedBookId === bookId
  }

  /** Reproduce el fragmento en `pos`. */
  async function playCurrent(): Promise<void> {
    const chunk = chunks[pos]
    if (!chunk) {
      cb.onEnd()
      return
    }
    // Cola ajena: ni un fragmento. Se sale ANTES de tocar `generation`, así
    // este intento no aborta de rebote la carga que está en curso — que es
    // exactamente la que va a dejar la cola en su sitio.
    if (!queueIsCurrent()) return
    // Esta reproducción toma el mando (ver `generation`). Pedir audio es una
    // intención de escucha: cancela la pausa que hubiera dejado la anterior.
    const gen = ++generation
    paused = false
    // El fragmento ANTERIOR sigue en el `<audio>` y sonando, y conseguir el
    // siguiente tarda los segundos de la API. Si se le deja llegar a su final
    // en esa ventana, su `'ended'` dispara `next()`: `pos` avanza otra vez y
    // el fragmento que se está sintetizando —y pagando— se salta para
    // siempre. Basta con pulsar ▶▶ en los últimos segundos de un fragmento.
    // Callarlo aquí también evita el solape (el viejo sonando mientras entra
    // el nuevo), y deja el `<audio>` sin `src`, que es justo lo que los bails
    // de abajo necesitan para que `resume()` reintente ESTE fragmento.
    clearAudio()
    try {
      // `blobFor` reclama la precarga en vuelo de este mismo fragmento (o la
      // de otro `playCurrent` concurrente) en vez de duplicar la síntesis.
      const blob = await blobFor(chunk)
      // Mientras se sintetizaba llegó otra orden (otro fragmento, otro libro
      // o un `stop()`): rendirse ANTES de tocar el `<audio>`, o este blob
      // sonaría encima del que el usuario pidió después.
      if (gen !== generation) return
      // Pausó mientras se sintetizaba: no arrancar nada. El `<audio>` ya está
      // vacío desde arriba, así que `resume()` reintenta ESTE fragmento (ya
      // pagado y cacheado) en vez de reanudar el `src` del anterior.
      if (paused) return

      if (currentUrl) URL.revokeObjectURL(currentUrl)
      currentUrl = URL.createObjectURL(blob)
      audio.src = currentUrl
      audio.playbackRate = rate
      await audio.play()
      if (gen !== generation) return

      cb.onChunk(chunk, pos, chunks.length)
      if (chunk.chapterHref !== lastChapter) {
        lastChapter = chunk.chapterHref
        cb.onChapter(chunk.chapterHref)
      }
      prefetchNext()
    } catch (e) {
      // Un fallo de una reproducción ya abandonada no es del usuario: ni se
      // avisa ni se silencia el `<audio>`, que ya es de otra generación.
      if (gen !== generation) return
      // Dejar el `<audio>` vacío: si el fallo fue al conseguir el blob, su
      // `src` seguiría apuntando al fragmento ANTERIOR, así que un `resume()`
      // lo reproduciría otra vez y, al terminar, el listener 'ended' llamaría
      // a `next()` — el fragmento que falló se saltaría para siempre. Sin
      // `src`, `resume()` reintenta ESTE fragmento, que es lo que el usuario
      // espera tras un fallo de red.
      clearAudio()
      // Si el usuario acaba de pausar, el fallo puede ser nuestro propio
      // `audio.pause()` abortando el `play()` en curso: eso no es un error
      // del que avisarle con un toast.
      if (paused) return
      cb.onError(e)
    }
  }

  /** Silencia el `<audio>` y suelta su ObjectURL (sin tocar la cola). */
  function clearAudio() {
    audio.pause()
    audio.removeAttribute('src')
    if (currentUrl) {
      URL.revokeObjectURL(currentUrl)
      currentUrl = null
    }
  }

  async function load(id: string): Promise<number> {
    bookId = id
    // El motor debe quedar limpio por sí mismo: si algo sonaba (de este
    // libro en otra posición, o de otro libro) no debe seguir haciéndolo,
    // y su ObjectURL no debe quedar colgado hasta la siguiente reproducción
    // con éxito. No confiar en que quien llame recuerde invocar stop()
    // antes de cargar otro libro.
    stop()
    // La cola de antes deja de valer AQUÍ, no cuando lleguen los fragmentos
    // nuevos: mientras esta carga esté en vuelo el motor no tiene cola de
    // nadie, y nadie puede darla por buena.
    queuedBookId = null
    // `stop()` acaba de invalidar lo que hubiera en vuelo; esta carga es la
    // vigente a partir de aquí.
    const gen = generation
    // El progreso se pide EN PARALELO con los fragmentos, y aquí y no en el
    // store: así el `load()` de recuperación de `play()` —cola robada— también
    // retoma por donde iba, y con el porcentaje más fresco (el que
    // `persistNeuralProgress` va escribiendo mientras se escucha) en vez de
    // uno que hubiera que recordar de la carga anterior.
    const [loaded, book] = await Promise.all([
      repo.getIndexChunksForBook(id).then(sortBookChunks),
      repo.getBook(id),
    ])
    // Otra carga (o un `stop()`) entró mientras leíamos los fragmentos: dos
    // `load()` concurrentes pueden resolver en desorden, y aplicar esta cola
    // ahora le dejaría al motor la del libro que el usuario ya abandonó. Se
    // devuelve la cuenta igual (es cierta), pero la cola no se toca: por eso
    // el número NO sirve para saber de quién es la cola, y existe
    // `queuedBook()`.
    if (gen !== generation) return loaded.length
    chunks = loaded
    // Sin fragmentos no hay cola que reclamar: así el próximo intento vuelve
    // a cargar (y quien corresponda, a indexar) en vez de darla por buena.
    queuedBookId = loaded.length > 0 ? id : null
    pos = startChunk(book?.progress.percent ?? 0, loaded.length)
    lastChapter = null
    return chunks.length
  }

  function queuedBook(): string | null {
    return queuedBookId
  }

  async function play(): Promise<void> {
    // Se recarga cuando la cola montada no es la del libro pedido —la verdad
    // del propio motor—, no cuando está vacía: `chunks.length` es un proxy que
    // da por buena la cola del libro ANTERIOR mientras un `load()` está en
    // vuelo. Y es lo que hace este estado RECUPERABLE y no solo seguro: sin
    // recargar, `playCurrent` se rendiría por cola ajena y el reproductor se
    // quedaría mudo para siempre.
    if (!queueIsCurrent() && bookId) await load(bookId)
    await playCurrent()
  }

  function pause() {
    // La marca es lo único que ve un `playCurrent` que todavía está
    // esperando su síntesis; el `audio.pause()` solo sirve si ya hay audio.
    paused = true
    audio.pause()
  }

  async function resume(): Promise<void> {
    paused = false
    // Si hay src, reanudar donde estaba.
    if (audio.src) {
      try {
        await audio.play()
      } catch (e) {
        cb.onError(e)
      }
      return
    }
    // Sin audio que reanudar, reanudar ES arrancar: se delega en `play()`, no
    // en `playCurrent()`. Si no, con la cola ajena (una carga abandonada)
    // `playCurrent` se rinde en silencio y el usuario se queda pulsando play
    // sin que suene nunca nada.
    await play()
  }

  /**
   * Reproduce el fragmento al que se acaba de mover... salvo que el usuario
   * tenga la reproducción en pausa, en cuyo caso saltar MUEVE la posición y
   * se queda en pausa.
   *
   * Arrancar por su cuenta dejaba al motor sonando con el store diciendo
   * 'paused' (que no toca el estado al delegar un `next()`/`prev()`): icono de
   * play, MediaSession en 'paused' y audio de fondo. Y es de lo más
   * alcanzable, porque en pausa anterior/siguiente siguen habilitados en las
   * dos barras y en la pantalla de bloqueo. El motor navegador ya se comporta
   * así —`speechSynthesis` sigue pausado y lo que encola no suena—, así que
   * esto además iguala los dos motores en vez de pedirle al store que
   * distinga.
   *
   * Se vacía el `<audio>`, no se deja el `src` viejo: `resume()` reanuda lo
   * que haya, y con el fragmento anterior todavía puesto repetiría ESE y, al
   * acabar, encadenaría con `next()` — el fragmento al que el usuario saltó se
   * perdería (mismo motivo que en el bail de `playCurrent`).
   */
  async function playUnlessPaused(): Promise<void> {
    if (paused) {
      clearAudio()
      return
    }
    await playCurrent()
  }

  async function next(): Promise<void> {
    // Sobre una cola ajena no se decide nada, y menos el final del libro: ese
    // `onEnd()` de aquí abajo sería el del libro que ya nadie escucha, y
    // dispara `stop()` en el store —libro a `null`, 'idle', reproductor
    // desaparecido— además de abortar la carga en curso. Pasa pulsando ▶▶ en
    // los auriculares mientras la mini-barra dice «Preparando…» (MediaSession
    // no pasa por los botones deshabilitados), y pasa solo cuando el último
    // fragmento del libro anterior termina justo entonces (listener 'ended').
    if (!queueIsCurrent()) return
    if (pos >= chunks.length - 1) {
      cb.onEnd()
      return
    }
    pos++
    await playUnlessPaused()
  }

  async function prev(): Promise<void> {
    // Simetría con `next()`: sin cola propia no hay posición que mover.
    if (!queueIsCurrent()) return
    if (pos === 0) {
      await playUnlessPaused()
      return
    }
    pos--
    await playUnlessPaused()
  }

  function stop() {
    // Las síntesis en vuelo (`inFlight`) NO se descartan: ya están pagadas y
    // se guardan en caché igualmente, así que olvidarlas solo conseguiría
    // que la siguiente petición del mismo fragmento las pagara otra vez. Lo
    // que sí se descarta es la REPRODUCCIÓN en vuelo: parar significa que
    // nada de lo que estuviera esperando su audio debe llegar a sonar.
    generation++
    clearAudio()
  }

  function setRate(value: number) {
    rate = value
    audio.playbackRate = value
  }

  /**
   * Salta `seconds` (relativo y con signo: −15 al retroceder, +15 al avanzar)
   * dentro del fragmento que suena.
   *
   * Es **asimétrico a propósito**, y el motivo es que los dos extremos no
   * significan lo mismo:
   *
   *   - Hacia atrás se acota en 0. El principio del fragmento es un sitio
   *     donde quedarse: se sigue oyendo el mismo audio.
   *   - Hacia delante NO se acota, se **rechaza** el salto que no cabe. El
   *     final del fragmento no es un sitio donde quedarse: el final SIGNIFICA
   *     avanzar. Acotar el destino al final —o a un pelo del final— solo
   *     retrasa el cruce, porque el `<audio>` sigue sonando y llega ahí por su
   *     cuenta en unos milisegundos; entonces el navegador dispara `'ended'`,
   *     este motor encadena con `next()` y el usuario acaba en el fragmento
   *     siguiente (síntesis que no pidió) o, en el último del libro, con el
   *     reproductor cerrado (`onEnd()` → `stop()` en el store) por haber
   *     pulsado «avanzar». Ver `SEEK_MIN_TAIL_S`.
   *
   * Las otras dos vías se cierran igual, y no por casualidad: son la misma
   * pregunta —«¿cabe el salto en lo que queda?»— sin respuesta fiable. Si no
   * han llegado los metadatos (`duration` NaN) no se sabe, y dejarlo pasar sin
   * tope hace que el navegador acote a la duración exacta y termine el
   * fragmento en el acto. Y en un fragmento más corto que la cola mínima no
   * cabe nunca.
   *
   * Lo que NO se toca es el encadenado normal: que la reproducción llegue al
   * final sonando sigue pasando al fragmento siguiente. Desarmar el listener
   * de `'ended'` sería la otra forma de cumplir «el salto no cruza», y rompe
   * el libro: el fragmento acabaría y no seguiría nada, con el store diciendo
   * 'playing' en silencio — el bug más viejo de esta rama.
   *
   * ponytail: el precio es que en los últimos ~15 s del fragmento el botón no
   * hace nada (el 12 % de un fragmento de 2 min), y no hay forma de que la UI
   * lo anticipe sin exponer la posición del `<audio>` al store. Vía de mejora:
   * cruzar de verdad —`pos++`/`pos--` con el `currentTime` colocado en el
   * fragmento vecino—, que es lo que la decisión de alcance dejó fuera.
   */
  function seek(seconds: number) {
    // Misma regla que `next`, `prev` y `playCurrent`: sobre una cola ajena no
    // se toca nada. Hoy el `src` de abajo lo cubriría por otro camino, pero la
    // invariante «de quién es la cola» tiene UN solo mecanismo en este motor, y
    // que cada arista pública se invente el suyo es lo que abrió las cuatro
    // puertas anteriores.
    if (!queueIsCurrent()) return
    // Sin `src` no hay posición que mover: entre fragmentos, tras un fallo de
    // síntesis o tras un `stop()` el `<audio>` está vacío y su `currentTime`
    // no significa nada (escribirlo no haría sonar nada, solo mentiría).
    if (!audio.src) return
    if (seconds < 0) {
      audio.currentTime = Math.max(0, audio.currentTime + seconds)
      return
    }
    if (!Number.isFinite(audio.duration)) return
    const target = audio.currentTime + seconds
    if (target > audio.duration - SEEK_MIN_TAIL_S) return
    audio.currentTime = target
  }

  return { load, queuedBook, play, pause, resume, next, prev, stop, setRate, seek }
}
